import React, { useEffect, useState } from 'react'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import { productOrderAcknowledgmentSchema } from '@utils/schemas'
import { useAppDispatch } from '@hooks/reduxHooks'
import { enqueueSnackbar } from '@state/reducers/alertReducer'
import { FormHeader } from '../FormHeader'
import { FilePreview } from '@components/file/FilePreview'
import { acknowledgeProductOrder } from '@state/reducers/productOrderReducer'
import { CreateProductOrderAcknowledgmentDto, ProductOrderDto } from '@services/dtos'
import { SimpleFileInput } from '@components/inputs/SimpleFileInput'
import { truncate } from 'lodash'
import InputField from '@components/inputs/InputField'
import { Box } from '@mui/material'

interface Props {
  productOrder: ProductOrderDto
  onClose: () => void
}

const ProductOrderAcknowledgmentForm: React.FC<Props> = ({ productOrder, onClose }) => {
  const dispatch = useAppDispatch()
  const [isSubmitting, setIsSubmitting] = useState(false)

  const {
    handleSubmit,
    reset,
    watch,
    setValue,
    formState: { errors },
    control
  } = useForm<CreateProductOrderAcknowledgmentDto>({
    resolver: yupResolver(productOrderAcknowledgmentSchema),
    defaultValues: {
      supplierOrderNumber: productOrder.supplierOrderNumber,
    }
  })

  const file = watch('file')
  const cb = async (msg: string) => {
    dispatch(
      enqueueSnackbar({
        message: msg,
        options: { variant: 'success' },
      }),
    )
  }

  useEffect(() => {
    return () => {
      reset()
    }
  }, [dispatch])

  const onSubmitForm = async (data: CreateProductOrderAcknowledgmentDto) => {
    setIsSubmitting(true)
    dispatch(
      acknowledgeProductOrder({
        id: productOrder.id,
        file: data.file,
        supplierOrderNumber: data.supplierOrderNumber,
      }),
    )
      .unwrap()
      .then(() => {
        cb('L\'ARC a été enregistré avec succès')
        onClose()
      })
      .catch(() => cb(`Une erreur est survenue.${data.supplierOrderNumber ? ' Vérifiez le N° de commande fournisseur et réessayez.' : ''}`))
      .finally(() => setIsSubmitting(false))
  }

  return (
    <div className="flex w-full h-full p-4">
      <form
        onSubmit={handleSubmit(onSubmitForm)}
        className="w-full h-full flex flex-col"
      >
        <FormHeader
          isSubmitting={isSubmitting}
          onClose={onClose}
          title={'Réception ARC'}
          subTitles={[
            `Commande ${productOrder.internalOrderNumber}`,
            `${truncate(productOrder.supplier.name, { length: 25, omission: '…' })} 
            - ${new Date(productOrder.createdAt).toLocaleDateString('fr-FR')}`
          ]}
        />

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 2 }}>
          <InputField
            control={control}
            name={'supplierOrderNumber'}
            label={'N° commande fournisseur'}
            type={'text'}
            error={!!errors.supplierOrderNumber?.message}
            helperText={errors.supplierOrderNumber?.message}
            sx={{ width: 300 }}
          />
          <SimpleFileInput 
            file={file} 
            onSelect={(file) => file && setValue('file', file)} 
            canRemove={false} 
            error={errors.file?.message} 
          />
        </Box>
        <div className="min-h-[300px] flex items-center justify-center h-[65vh] mt-4">
          <FilePreview file={file} />
        </div>
      </form>
    </div>
  )
}

export default ProductOrderAcknowledgmentForm
